import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Bot } from 'lucide-react';
import { motion } from 'framer-motion';

const fadeUp = {
  hidden:   { opacity: 0, y: 28 },
  visible:  (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.11, duration: 0.65, ease: [0.22, 1, 0.36, 1] },
  }),
};

const CorporateHero = () => {
  const [primaryHover, setPrimaryHover] = useState(false);
  const [secondaryHover, setSecondaryHover] = useState(false);

  return (
    <section
      style={{
        background: 'linear-gradient(160deg, #0a0118 0%, #1e0a3c 40%, #0d1b4b 70%, #0a0118 100%)',
        position: 'relative',
        overflow: 'hidden',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {/* ── Grid overlay ── */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
          backgroundImage: `
            linear-gradient(rgba(99,102,241,0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(99,102,241,0.08) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      {/* ── Glow circle 1 — indigo, top-right ── */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: -120,
          right: -120,
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,0.25) 0%, transparent 60%)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      {/* ── Glow circle 2 — violet, bottom-left ── */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: -80,
          left: -80,
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139,92,246,0.2) 0%, transparent 60%)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      {/* ── Floating UI cards (desktop only) ── */}

      {/* Automation card — bottom left */}
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: 1.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="hidden lg:block"
        style={{
          position: 'absolute',
          bottom: '20%',
          left: '6%',
          width: 192,
          zIndex: 10,
          background: 'rgba(255,255,255,0.07)',
          border: '1px solid rgba(255,255,255,0.12)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)',
          padding: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Zap size={13} color="white" />
          </div>
          <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, fontWeight: 600 }}>Automation live</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {['Lead scoring', 'Invoice flow', 'CRM sync'].map((t, i) => (
            <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: i === 1 ? '#a78bfa' : '#34d399', flexShrink: 0 }} />
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, flex: 1 }}>{t}</span>
              <span style={{ fontSize: 10, fontWeight: 600, color: i === 1 ? '#a78bfa' : '#34d399' }}>{i === 1 ? 'Kör...' : '✓'}</span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* AI Agent card — upper left */}
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: 1.3, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="hidden lg:block"
        style={{
          position: 'absolute',
          top: '30%',
          left: '5%',
          width: 176,
          zIndex: 10,
          background: 'rgba(255,255,255,0.07)',
          border: '1px solid rgba(255,255,255,0.12)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)',
          padding: '14px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div style={{ width: 24, height: 24, borderRadius: 6, background: 'linear-gradient(135deg, #22d3ee, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Bot size={12} color="white" />
          </div>
          <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11, fontWeight: 600, flex: 1 }}>AI Agent</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span className="animate-pulse" style={{ width: 6, height: 6, borderRadius: '50%', background: '#34d399', display: 'inline-block' }} />
            <span style={{ color: '#34d399', fontSize: 10 }}>Live</span>
          </span>
        </div>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, lineHeight: 1.6 }}>
          Svarar kunder, loggar ärenden, eskalerar undantag.
        </p>
      </motion.div>

      {/* ── Main content ── */}
      <div className="container mx-auto px-6 pt-24 pb-20" style={{ position: 'relative', zIndex: 10 }}>
        <div style={{ maxWidth: 780, margin: '0 auto', textAlign: 'center' }}>

          {/* Badge */}
          <motion.div custom={0} initial="hidden" animate="visible" variants={fadeUp}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase' as const,
              padding: '6px 16px',
              borderRadius: 100,
              marginBottom: 28,
              background: 'rgba(99,102,241,0.15)',
              border: '1px solid rgba(99,102,241,0.35)',
              color: '#a5b4fc',
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#818cf8', display: 'inline-block' }} />
              Automation · SaaS · AI-verktyg
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            custom={1} initial="hidden" animate="visible" variants={fadeUp}
            style={{
              color: '#FFFFFF',
              fontWeight: 800,
              fontSize: 'clamp(36px, 6vw, 72px)',
              lineHeight: 1.05,
              letterSpacing: '-0.025em',
              marginBottom: 24,
            }}
          >
            Sluta göra manuellt arbete<br />
            <span style={{
              background: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #60a5fa 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              som kan automatiseras
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            custom={2} initial="hidden" animate="visible" variants={fadeUp}
            style={{ color: '#94a3b8', fontSize: 18, lineHeight: 1.7, maxWidth: 580, margin: '0 auto 40px' }}
          >
            Vi bygger system som gör jobbet åt dig. Automation, AI och SaaS för B2B-företag — leverans inom 2–4 veckor.
          </motion.p>

          {/* CTAs */}
          <motion.div
            custom={3} initial="hidden" animate="visible" variants={fadeUp}
            style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'flex-start' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <Link to="/contact">
                <button
                  onMouseEnter={() => setPrimaryHover(true)}
                  onMouseLeave={() => setPrimaryHover(false)}
                  style={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    border: 'none',
                    color: 'white',
                    padding: '14px 28px',
                    borderRadius: 10,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    boxShadow: primaryHover
                      ? '0 0 40px rgba(99,102,241,0.7), 0 4px 16px rgba(0,0,0,0.3)'
                      : '0 0 24px rgba(99,102,241,0.5), 0 4px 16px rgba(0,0,0,0.3)',
                    transition: 'all 0.25s ease',
                  }}
                >
                  Beskriv ditt behov
                  <ArrowRight size={16} />
                </button>
              </Link>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Svar inom 24 timmar</span>
            </div>
            <Link to="/ai-consulting">
              <button
                onMouseEnter={() => setSecondaryHover(true)}
                onMouseLeave={() => setSecondaryHover(false)}
                style={{
                  background: secondaryHover ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: 'white',
                  padding: '14px 28px',
                  borderRadius: 10,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.25s ease',
                }}
              >
                Se våra produkter
              </button>
            </Link>
          </motion.div>

          {/* Trust pill */}
          <motion.div custom={4} initial="hidden" animate="visible" variants={fadeUp} style={{ marginTop: 56 }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              flexWrap: 'wrap' as const,
              justifyContent: 'center',
              gap: 16,
              padding: '10px 24px',
              borderRadius: 100,
              border: '1px solid rgba(255,255,255,0.10)',
              background: 'rgba(255,255,255,0.06)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}>
              {['Baserat i Sverige', 'Leverans 2–4 veckor', 'Direktkontakt med utvecklare'].map((item, i) => (
                <span key={item} style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>{item}</span>
                  {i < 2 && <span style={{ width: 1, height: 12, background: 'rgba(255,255,255,0.15)', display: 'inline-block' }} />}
                </span>
              ))}
            </div>
          </motion.div>

        </div>
      </div>

      {/* ── SVG wave divider (hero → LogoBand) ── */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, lineHeight: 0, zIndex: 11, marginBottom: '-2px' }}>
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', width: '100%', border: 'none', outline: 'none' }}>
          <path d="M0,60 C360,0 1080,0 1440,60 L1440,60 L0,60 Z" fill="#F8FAFC" />
        </svg>
      </div>
    </section>
  );
};

export default CorporateHero;
